import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getProjectsByService } from "../data/projects";

const SolutionsMetier = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      num: "01",
      title: "Logiciels Métier sur Mesure",
      description:
        "Des outils conçus pour un métier précis, à partir de vos processus réels plutôt que d'un standard à contourner.",
      features: [
        "Analyse des processus existants",
        "Modélisation des données métier",
        "Interfaces adaptées au terrain",
      ],
    },
    {
      num: "02",
      title: "Systèmes d'Information",
      description:
        "La colonne vertébrale qui relie vos services : une donnée saisie une fois, disponible partout où elle compte.",
      features: [
        "Référentiel de données unifié",
        "Gestion des rôles et des droits",
        "Traçabilité des opérations",
      ],
    },
    {
      num: "03",
      title: "Plateformes Multi-Sites",
      description:
        "Piloter un réseau d'établissements ou de points de vente depuis une console unique, sans perdre le détail local.",
      features: [
        "Consolidation multi-entités",
        "Tableaux de bord par site",
        "Reporting centralisé",
      ],
    },
    {
      num: "04",
      title: "Intégration & Reprise de Données",
      description:
        "Connecter le nouveau système à l'existant et récupérer l'historique, pour que la bascule ne reparte pas de zéro.",
      features: [
        "Connexion aux outils en place",
        "Migration de l'historique",
        "Accompagnement au déploiement",
      ],
    },
  ];

  const secteurs = [
    {
      titre: "Collectivités",
      texte:
        "Gestion des signalements citoyens, priorisation des interventions et suivi des équipes terrain.",
    },
    {
      titre: "Santé",
      texte:
        "Centralisation du parcours patient, coordination des services et lecture de l'activité de l'établissement.",
    },
    {
      titre: "Retail & Réseaux",
      texte:
        "Stocks, encaissements et performance commerciale consolidés sur l'ensemble des points de vente.",
    },
  ];

  const projects = getProjectsByService("solutions-metier");

  return (
    <div className="bg-gray-950 text-white min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end pb-20">
        <div className="absolute inset-0">
          <img
            src="/images/main/2149755776.jpg"
            alt="Solutions Métier"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/70 to-gray-950/30" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 w-full">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-1 border border-flowkraft-primary/30 text-flowkraft-secondary text-sm mb-6">
              Services
            </div>
            <h1 className="font-display text-display text-white mb-6">
              Solutions Métier
            </h1>
            <p className="text-xl text-gray-300 max-w-xl">
              Des logiciels et des systèmes conçus pour un secteur, pas pour tout
              le monde
            </p>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-display text-title text-white mb-6">
                Notre Expertise
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Un logiciel métier ne se juge pas à sa liste de fonctionnalités,
                mais à sa capacité à épouser une organisation qui existe déjà.
                Nous partons du terrain — les gestes, les contraintes, les
                habitudes — avant d'écrire la première ligne de code.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed">
                Nos plateformes sont pensées pour durer : données structurées,
                droits maîtrisés, montée en charge anticipée. Elles s'intègrent à
                l'existant plutôt que de le remplacer d'un bloc.
              </p>
            </div>
            <div className="relative">
              <img
                src="/images/consulting/20618.jpg"
                alt="Solutions métier"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 border border-white/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-title text-white mb-16">
            Nos Services
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="border border-white/10 p-8 hover:border-flowkraft-primary/50 transition-colors"
              >
                <span className="font-display text-4xl text-flowkraft-primary/30 mb-4 block">
                  {service.num}
                </span>
                <h3 className="font-display text-2xl text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-400 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-gray-300 text-sm">
                      <Check className="w-4 h-4 text-flowkraft-secondary mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Secteurs */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-title text-white mb-16">
            Les Secteurs Que Nous Outillons
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {secteurs.map((secteur, index) => (
              <div key={index} className="border-l border-flowkraft-primary/30 pl-6">
                <h3 className="font-display text-2xl text-white mb-4">
                  {secteur.titre}
                </h3>
                <p className="text-gray-400 leading-relaxed">{secteur.texte}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-title text-white mb-16">
            Nos Réalisations
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <div key={index} className="group relative overflow-hidden aspect-[4/3] bg-gray-900">
                <img
                  src={project.image}
                  alt={project.title}
                  onError={(e) => {
                    e.currentTarget.style.visibility = "hidden";
                  }}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/50 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <h3 className="font-display text-xl text-white mb-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">{project.summary}</p>
                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-flowkraft-secondary text-sm font-medium"
                    >
                      Voir le projet
                      <ArrowUpRight className="w-4 h-4 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/main/cta_background_digital.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <h2 className="font-display text-title text-white mb-6">
            Un métier à outiller ?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Parlons de vos processus avant de parler de logiciel
          </p>
          <Link to="/#contact" className="btn-solid inline-flex">
            Demander un devis
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SolutionsMetier;
